import React, { useState, useEffect, lazy, Suspense, useMemo } from 'react';
import { Stack,SearchBox, Text,Dropdown,DatePicker,IDatePickerStrings,Shimmer, Icon} from '@fluentui/react';
import {  IconButton } from '@fluentui/react/lib/Button';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import * as Constants from "../../../utils/constants";
import {
  ServiceContext,
} from "@msx/platform-services";
import { getConsumptionAPI } from "../../../utils/httpUtils";
import "./TicketDetails.css";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrUserData,
  getTicketDetailsData,
  SETChileComponentName,
  SETHasServiceRequestTypeLogData,
  SETIsCustomeLog,
  SETLogEventData,
  SETServiceRequestTypeLogData,
} from "../../../../core/store";
import { lazyRetry } from '../../../utils/utils';
import { FilterFilled } from "@fluentui/react-icons";

const TicketDetails = lazy(() => lazyRetry(() => import("./TicketDetails")));
const StatusTile = lazy(() => lazyRetry(() => import("./StatusTile")));

// Constants for Japan warning configuration
const JAPAN_WARNING_CONFIG = {
  AREA_NAME: "Japan",
  SNOW_URL: "https://microsoftintl.service-now.com/external?id=external_index",
  MESSAGE: {
    PREFIX: "Intermittently MCAPSHelp dashboard is unable to render Incident details for Japan ARC services. Please use the",
    LINK_TEXT: "SNOW",
    SUFFIX: "interface to get your latest update on the ticket."
  }
} as const;

const LINK_STYLES = {
  color: "#005a9e",
  textDecoration: "underline"
} as const;

// Separate component for Japan warning message
const JapanWarningMessage: React.FC<{ isZoomed: boolean }> = ({ isZoomed }) => (
  <div className="japan-warning-wrapper">
    <div className={`japan-warning-message ${isZoomed ? "zoom-125" : ""}`}>
      <span>{JAPAN_WARNING_CONFIG.MESSAGE.PREFIX} </span>
      <a
        href={JAPAN_WARNING_CONFIG.SNOW_URL}
        target="_blank"
        rel="noopener noreferrer"
        style={LINK_STYLES}
        aria-label="ServiceNow external portal for Japan ARC services"
      >
        {JAPAN_WARNING_CONFIG.MESSAGE.LINK_TEXT}
      </a>
      <span> {JAPAN_WARNING_CONFIG.MESSAGE.SUFFIX}</span>
    </div>
  </div>
);

export interface TicketDetailsProp extends InjectedIntlProps {
  userId: any;
  isUserLoggedIn: any;
  setActiveTabKey?: any;
  setIsTicketDetailVisible: any;
  status: any;
  activeTabKey:any;
  setActiveTicketCount:any;
  loadTicketData?: () => Promise<void> | void;
}

const TicketDetailsComponent: React.FC<TicketDetailsProp> = (props) => {
  const [isLoading, setLoading] = useState(true);
  const [closedTicketCount, setClosedTicketCount] = useState(0);
  const [activeTicketCount, setActiveTicketCount] = useState(0);
  const usersettings = useSelector(getCurrUserData);
  //const context = React.useContext(ServiceContext);

  //const { data, updateContextData } = useMyContext();

  const [status, setStatus] = useState(props.status);
  const [createdOn, setCreatedOn] = useState(null);
  const [targetSystem, setTargetSystem] = useState("");
  const [optionsTargetSystems, setOptionsTargetSystems] = useState([]);
  const [optionsStatus, setOptionsStatus] = useState([]);

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const ticketDetailsData = useSelector(getTicketDetailsData);
  const maxDate = new Date();
  const reduxDispatch = useDispatch();

  // Ensure tickets are loaded when this component is shown (e.g., direct /mytickets navigation)
  useEffect(() => {
    if (props.loadTicketData && props.isUserLoggedIn) {
      if (!ticketDetailsData || (Array.isArray(ticketDetailsData) && ticketDetailsData.length === 0)) {
        props.loadTicketData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.loadTicketData, props.isUserLoggedIn]);

  // Memoized check for Japan user
  const isJapanUser = useMemo(() => 
    usersettings?.userAreaName === JAPAN_WARNING_CONFIG.AREA_NAME, 
    [usersettings?.userAreaName]
  );

  const formatDate: IDatePickerStrings = {
    shortMonths: [
      "01",
      "02",
      "03",
      "04",
      "05",
      "06",
      "07",
      "08",
      "09",
      "10",
      "11",
      "12",
    ],
    months: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    shortDays: ["S", "M", "T", "W", "T", "F", "S"],
    days: [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ],
    goToToday: "Go to today",
    prevMonthAriaLabel: "Go to previous month",
    nextMonthAriaLabel: "Go to next month",
    prevYearAriaLabel: "Go to previous year",
    nextYearAriaLabel: "Go to next year",
    closeButtonAriaLabel: "Close date picker",
  };

  const formatSelectedDate = (date: Date | null): string => {
    if (!date) {
      return "";
    }

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(2);

    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    reduxDispatch(SETChileComponentName("My Help Dashboard"));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "My Help Dashboard ",
        elementId: "",
        action: "Click",
        message: `User open My Help Dashboard page `,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));

    // Use Redux data instead of API call
      checkDataResponse(ticketDetailsData);
  }, [ticketDetailsData]);


  const checkDataResponse = (responseValue) => {
    // Ensure we always have an array for processing
    let dataArray = [];
    if (Array.isArray(responseValue)) {
      dataArray = responseValue;
    } else if (responseValue && typeof responseValue === 'object' && Array.isArray(responseValue.data)) {
      dataArray = responseValue.data;
    } else {
      dataArray = [];
    }
    
    bindStatus(dataArray);
    bindTargetSystem(dataArray);
    calculateDataForTicket(dataArray);
    setLoading(false)
    //updateContextData(responseValue);
  };


  const bindStatus = (data) => {
    let itemStatus = [{ key: "All", text: "All", value: "All" }];
    
    // Ensure data is an array before calling forEach
    if (!Array.isArray(data)) {
      return;
    }

    data.forEach((e) => {
      const lowerItemStatus = e?.status?.toLowerCase() || '';
      if (!lowerItemStatus) {
        return;
      }
      const itemStatusExist = itemStatus.some(
        (item) => item.key.toLowerCase() === lowerItemStatus
      );
      if (!itemStatusExist) {
        itemStatus.push({ key: e.status, text: e.status, value: e.status });
      }
    });

    setOptionsStatus(itemStatus);
  };

  const bindTargetSystem = (data) => {
    let targetsystems = [{ key: "", text: "All", value: "" }];
    
    // Ensure data is an array before calling forEach
    if (!Array.isArray(data)) {
      return;
    }

    data.forEach((e) => {
      const lowercaseTargetSystem = e?.targetSystem?.toLowerCase() || '';
      if (!lowercaseTargetSystem) {
        return;
      }
      const targetSystemExists = targetsystems.some(
        (item) => item.key.toLowerCase() === lowercaseTargetSystem
      );
      if (!targetSystemExists) {
        targetsystems.push({
          key: e.targetSystem,
          text: e.targetSystem,
          value: e.targetSystem,
        });
      }
    });

    setOptionsTargetSystems(targetsystems);
  };

  const checkResponse = (response) => {
    switch (response.status) {
      case 200:
        if (typeof response.data.content.value === "object") {
          return "success";
        } else if (
          response.data.content.value === "Error from Azure Function: Not Found"
        ) {
          setActiveTicketCount(0);
          setClosedTicketCount(0);

          return "successFromValue";
        } else {
          return "errorFromValue";
        }
      case 201:
      case 204:
        return "success";
      case 401:
      default:
        return "error";
    }
  };

  const calculateDataForTicket = (data) => {
    setActiveTicketCount(0); // Reset counters
    setClosedTicketCount(0);
    
    // Ensure data is an array before calling forEach
    if (!Array.isArray(data)) {
      return;
    }
    
    data.length > 0 &&
      data.forEach((e) => {
        var lowerCaseStatus = e?.status?.toLowerCase() || '';
        const lowerCaseTargetSystem = e?.targetSystem?.toLowerCase() || '';
        if (
            lowerCaseStatus === "closed" ||
          (lowerCaseTargetSystem === "snow" &&
            lowerCaseStatus === "resolved") ||
          (lowerCaseTargetSystem === "hrd365" &&
            lowerCaseStatus === "resolved") ||
          (lowerCaseTargetSystem === "uatracker" &&
            lowerCaseStatus === "done") ||
          (lowerCaseTargetSystem === "cosmic" &&
            (lowerCaseStatus === "closed" || lowerCaseStatus === "resolved" || lowerCaseStatus === "cancelled")) ||
          (lowerCaseTargetSystem === "gsoc" &&
            (lowerCaseStatus === "closed" || lowerCaseStatus === "resolved" || lowerCaseStatus === "cancelled"))
        ) {
          setClosedTicketCount((prevCount) => prevCount + 1);
        } else {
          setActiveTicketCount((prevCount) => prevCount + 1);
        }
      });
  };

  const gotoMyWorkspace = () => {
    reduxDispatch(SETChileComponentName("My Help Dashboard"));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "My Help Dashboard back",
        elementId: "",
        action: "Click",
        message: `User click on back button of My Help Dashboard page`,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    props.setIsTicketDetailVisible(false);
  };

  const handleClearFilter = () => {
    reduxDispatch(SETChileComponentName("My Help Dashboard"));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "My Help Dashboard Clear Filter",
        elementId: "",
        action: "Click",
        message: `User Clear Filter  on My Help Dashboard page `,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    setStatus("All");
    setCreatedOn(null);
    setTargetSystem("");
    setSearchText("");
  };

  const handleStatusChange = (event, option) => {
    reduxDispatch(SETChileComponentName("My Help Dashboard"));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "My Help Dashboard Status change",
        elementId: "",
        action: "on Change",
        message: `User Status change  on My Help Dashboard page `,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    setStatus(option.value);
  };

  const handleTrackingSystemChange = (event, option) => {
    reduxDispatch(SETChileComponentName("My Help Dashboard"));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "My Help Dashboard Tracking System Change",
        elementId: "",
        action: "on Change",
        message: `User Tracking System Change on My Help Dashboard page `,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    setTargetSystem(option.value);
  };

  const handleDateChange = (date: Date | null | undefined): void => {
    reduxDispatch(SETChileComponentName("My Help Dashboard"));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "My Help Dashboard Date Search",
        elementId: "",
        action: "on DateChange",
        message: `User Date Search  on My Help Dashboard page `,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    setCreatedOn(date || null); // Ensure null if undefined
  };
  const handleClearSearch = () => {
    reduxDispatch(SETChileComponentName("My Help Dashboard"));
    setSearchText("");
  };
  const handleSearhText = (searchtext) => {
    reduxDispatch(SETChileComponentName("My Help Dashboard"));
    reduxDispatch(SETHasServiceRequestTypeLogData(true));
    reduxDispatch(
      SETLogEventData({
        elementName: "My Help Dashboard Search",
        elementId: "",
        action: "on Change",
        message: `User Search  on My Help Dashboard page `,
      })
    );
    reduxDispatch(
      SETServiceRequestTypeLogData({
        SearchTerm: searchtext?.target?.value,
      })
    );
    reduxDispatch(SETIsCustomeLog(true));
    if (searchtext) {
      setSearchText(searchtext.target.value);
    } else {
      setSearchText("");
    }
  };
  
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    const detectZoom = () => {
      const zoom = Math.round(window.devicePixelRatio * 100);
      setIsZoomed(zoom >= 110); // You can fine-tune this threshold if needed
    };

    detectZoom();
    window.addEventListener("resize", detectZoom);
    return () => window.removeEventListener("resize", detectZoom);
  }, []);

  return (
    <>
      <div>
        <div className="dashboard-header">
          <div className="dashboard-back-button">
            <IconButton
              iconProps={{ iconName: "Back" }}
              title="Go to My Help Workspace"
              onClick={() => gotoMyWorkspace()}
              ariaLabel="Go to My Help Workspace"
            />
          </div>
          <h2 className="dashboard-heading">My Help Dashboard</h2>
        </div>

        {isLoading && (
          <Stack horizontal styles={{ root: { width: "200px", height: 89 } }}>
            <Shimmer width={200} height={89} />
          </Stack>
        )}
        {!isLoading && (
          <Suspense
            fallback={
             <></>
            }>
            {" "}
            <StatusTile
              status="Open"
              count={!isLoading?activeTicketCount:"--" }
              iconName="CircleRing"
            />{" "}
          </Suspense>
        )}
        
        {isLoading && (
          <Stack horizontal styles={{ root: { width: "200px", height: 89 } }}>
            <Shimmer width={200} height={89} />
          </Stack>
        )}
        {!isLoading && (
          <Suspense
            fallback={
             <></>
            }>
            <StatusTile
              status="Closed/Resolved"
              count={!isLoading?closedTicketCount: "--"  }
              iconName="Completed"
            />{" "}
          </Suspense>
        )}

        {/* Japan Warning - Clean and maintainable implementation */}
        {!isLoading && isJapanUser && (
          <JapanWarningMessage isZoomed={isZoomed} />
        )}

        {isLoading && (
          <Stack horizontal styles={{ root: { width: "100%", height: 20 } }}>
            <Shimmer width={1000} height={20} />
          </Stack>
        )}
        {!isLoading && (
          <Stack
            horizontal
            verticalAlign="center"
            horizontalAlign="space-between"
            tokens={{ childrenGap: 10 }}
            styles={{ root: { margin: "10px 0px", width: "90%" } }}>
              <div>
               <Icon iconName="Info" />
            <span
              style={{
                textAlign: "left",
                fontSize: "13px",
                // fontWeight: "bolder",
                fontStyle: "italic",
              }}>
              {Constants.TICKET_ESCALATION_MESSAGE}
            </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "left",
                justifyContent: "flex-start",
                gap:"10px"
              }}>
              <div className="search-box">
                <SearchBox
                  placeholder="Search..."
                  value={searchText}
                  onChange={handleSearhText}
                  aria-label="Search Box"
                  onClear={handleClearSearch}
                  styles={{
                    iconContainer: {
                      color:"#616161",
                      transform: "scaleX(-1)", // Flip the search icon horizontally
                    },}}
                />
              </div>
              {/* <DefaultButton 
          className='defaultButton' 
          text="Escalate a ticket with Copilot" 
        /> */}
              <div
                className="filter-button-section"
                onClick={() => setIsFilterVisible(!isFilterVisible)}>
               <FilterFilled style={{ fontSize: 20, color: "#0F6CBD" }} />
                <Text style={{ color: "#0F6CBD" }} variant="medium">Filter</Text>
              </div>
            </div>
          </Stack>
        )}

        {/* Filter section */}
        {isFilterVisible && (
          <div className="filter-section">
            {/* Dropdowns */}
            <div className="filter-dropdown-container">
              <Text className="filter-label" variant="smallPlus">Status</Text>
              <Dropdown
                options={optionsStatus}
                onChange={handleStatusChange}
                placeholder="Select Status"
                className="filter-dropdown"
                selectedKey={status}
                aria-label="Status Dropdown"
              />
            </div>
            <div className="filter-dropdown-container filter-date-container">
              <Text className="filter-label" variant="smallPlus">Created On</Text>
              <DatePicker
                value={createdOn}
                onSelectDate={handleDateChange}
                placeholder="Select Created On"
                className="filter-dropdown"
                formatDate={(date) => formatSelectedDate(date)}
                strings={formatDate}
                maxDate={maxDate}
                aria-label="Date Picker"
              />
            </div>
            <div className="filter-dropdown-container">
              <Text className="filter-label" variant="smallPlus">Target System</Text>
              <Dropdown
                options={optionsTargetSystems}
                onChange={handleTrackingSystemChange}
                placeholder="Select Target System"
                className="filter-dropdown"
                selectedKey={targetSystem}
                aria-label="Target System Dropdown"
              />
            </div>

            <div
              className="clear-filter"
              onClick={handleClearFilter}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleClearFilter();
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Clear All Filter"
            >
              <span className="clear-filter-text">Clear All</span>
            </div>
          </div>
        )}

        {isLoading && (
          <Stack horizontal styles={{ root: { width: "100%", height: 800 } }}>
            <Shimmer width={1000} height={800} />
          </Stack>
        )}
        {!isLoading && (
          <Suspense
            fallback={
              <></>
            }>
            <TicketDetails
              data={ticketDetailsData}
              status={status}
              targetSystem={targetSystem}
              createdOn={createdOn}
              searchText={searchText}
            />
          </Suspense>
        )}
      </div>
    </>
  );
};

export const TicketDetailPage = injectIntl(TicketDetailsComponent);

// Generated by Copilot
// updated on: 2026-07-29 | 8 lines changed | [Auto Generated] | Fixed My Tickets dashboard error-boundary crash: null-guarded status/targetSystem before .toLowerCase() in bindStatus & bindTargetSystem (ICM 842334546) | Time Taken 3 min
